import { PaginationList } from 'best-modules/components/server-table/types'
import { sendRequest } from 'best-modules/vue-utils'
import { Query } from 'best-modules/utils'
import {
  DocumentClassType,
  DocumentClassTypeCredentials,
} from '@/utils/types/documentClassType'
import {
  DocumentTypeAccessCredentials,
  DocumentTypeDefinition,
  DocumentTypeDefinitionCredentials,
} from '@/utils/types/documentType'
import {
  DocumentTypeRoute,
  DocumentTypeRouteCredentials,
  DocumentTypeRouteStage,
  DocumentTypeRouteStageCredentials,
  DocumentTypeRouteStageListItem,
  DocumentTypeRouteStageParticipant,
  DocumentTypeRouteStageParticipantCredentials,
} from '@/utils/types/documentRoute'
import {
  DocumentTypeField,
  DocumentTypeFieldCredentials,
} from '@/utils/types/documentTypeField'

const docTypeClassList = (
  query?: Query
): Promise<PaginationList<DocumentClassType>> => {
  return sendRequest({
    name: 'docTypeClassList',
    method: 'get',
    url: {
      url: '/json/doc-type/class',
      query,
    },
  })
}

const docTypeClassActivate = (id: number): Promise<DocumentClassType> => {
  return sendRequest({
    name: 'docTypeClassActivate',
    method: 'get',
    url: `/json/doc-type/class/activate/${id}`,
  })
}

const docTypeClassDeactivate = (id: number): Promise<DocumentClassType> => {
  return sendRequest({
    name: 'docTypeClassDeactivate',
    method: 'get',
    url: `/json/doc-type/class/deactivate/${id}`,
  })
}

const docTypeClassUpdate = (
  id: number,
  credentials: DocumentClassTypeCredentials
): Promise<DocumentClassType> => {
  return sendRequest({
    name: 'docTypeClassUpdate',
    method: 'post',
    url: `/json/doc-type/class/update/${id}`,
    payload: credentials,
  })
}

const docTypeClassCreate = (
  credentials: DocumentClassTypeCredentials
): Promise<DocumentClassType> => {
  return sendRequest({
    name: 'docTypeClassCreate',
    method: 'post',
    url: '/json/doc-type/class/create',
    payload: credentials,
  })
}

const docTypeClassFlowTypes = (): Promise<{ id: string; name: string }[]> => {
  return sendRequest({
    name: 'docTypeClassFlowTypes',
    method: 'get',
    url: '/json/doc-type/class/flow-types',
  })
}

const docTypeDefinitionList = (
  query: Query
): Promise<PaginationList<any[]>> => {
  return sendRequest({
    name: 'docTypeDefinitionList',
    method: 'get',
    url: {
      url: '/json/doc-type/definition',
      query,
    },
  })
}

const docTypeDefinitionCreate = (
  credentials: DocumentTypeDefinitionCredentials
): Promise<DocumentTypeDefinition> => {
  return sendRequest({
    name: 'docTypeDefinitionCreate',
    method: 'post',
    url: '/json/doc-type/definition/create',
    payload: credentials,
  })
}

const docTypeDefinitionUpdate = (
  docTypeId: number,
  credentials: DocumentTypeDefinitionCredentials
): Promise<DocumentTypeDefinition> => {
  return sendRequest({
    name: 'docTypeDefinitionUpdate',
    method: 'post',
    url: `/json/doc-type/definition/update/${docTypeId}`,
    payload: credentials,
  })
}

const docTypeDefinitionSingle = (id: number): Promise<any> => {
  return sendRequest({
    name: 'docTypeDefinitionSingle',
    method: 'get',
    url: `/json/doc-type/definition/single/${id}`,
  })
}

const docTypeDefinitionDelete = (id: number): Promise<any[]> => {
  return sendRequest({
    name: 'docTypeDefinitionDelete',
    method: 'delete',
    url: `/json/doc-type/definition/delete/${id}`,
  })
}

const docTypeDefinitionActivate = (id: number): Promise<void> => {
  return sendRequest({
    name: 'docTypeDefinitionActivate',
    method: 'get',
    url: `/json/doc-type/definition/activate/${id}`,
  })
}

const docTypeDefinitionDeactivate = (id: number): Promise<void> => {
  return sendRequest({
    name: 'docTypeDefinitionDeactivate',
    method: 'get',
    url: `/json/doc-type/definition/deactivate/${id}`,
  })
}

const docTypeAccessesUpdate = (
  docTypeId: number,
  accesses: DocumentTypeAccessCredentials[]
): Promise<DocumentTypeDefinition> => {
  return sendRequest({
    name: 'docTypeAccessesUpdate',
    method: 'post',
    url: `/json/doc-type/definition/${docTypeId}/accesses`,
    payload: {
      accesses,
    },
  })
}

const docTypeAccessCreate = (
  docTypeId: number,
  credentials: Partial<DocumentTypeAccessCredentials>
): Promise<DocumentTypeDefinition> => {
  return sendRequest({
    name: 'docTypeAccessCreate',
    method: 'post',
    url: `/json/doc-type/definition/${docTypeId}/accesses/create`,
    payload: credentials,
  })
}

const docTypeAccessDelete = (accessId: number): Promise<void> => {
  return sendRequest({
    name: 'docTypeAccessDelete',
    method: 'delete',
    url: `/json/doc-type/definition/accesses/${accessId}`,
  })
}

const docTypeRouteList = (
  documentDefinitionId: number
): Promise<
  Array<
    Omit<DocumentTypeRoute, 'stages'> & {
      stages: Array<DocumentTypeRouteStageListItem>
    }
  >
> => {
  return sendRequest({
    name: 'docTypeRouteList',
    method: 'get',
    url: `/json/doc-type/route/byDocTypeDefinition/${documentDefinitionId}`,
  })
}

const docTypeRouteSingle = (routeId: number): Promise<DocumentTypeRoute> => {
  return sendRequest({
    name: 'docTypeRouteSingle',
    method: 'get',
    url: `/json/doc-type/route/single/${routeId}`,
  })
}

const docTypeRouteCreate = (
  credentials: DocumentTypeRouteCredentials
): Promise<DocumentTypeRoute> => {
  return sendRequest({
    name: 'docTypeRouteCreate',
    method: 'post',
    url: '/json/doc-type/route/create',
    payload: credentials,
  })
}

const docTypeRouteUpdate = (
  routeId: number,
  credentials: Omit<DocumentTypeRouteCredentials, 'docTypeDefinitionId'>
): Promise<DocumentTypeRoute> => {
  return sendRequest({
    name: 'docTypeRouteUpdate',
    method: 'put',
    url: `/json/doc-type/route/update/${routeId}`,
    payload: credentials,
  })
}

const docTypeRouteActivate = (routeId: number): Promise<DocumentTypeRoute> => {
  return sendRequest({
    name: 'docTypeRouteActivate',
    method: 'patch',
    url: `/json/doc-type/route/activate/${routeId}`,
  })
}
const docTypeRouteDeactivate = (
  routeId: number
): Promise<DocumentTypeRoute> => {
  return sendRequest({
    name: 'docTypeRouteDeactivate',
    method: 'patch',
    url: `/json/doc-type/route/deactivate/${routeId}`,
  })
}

const docTypeRouteDelete = (routeId: number): Promise<void> => {
  return sendRequest({
    name: 'docTypeRouteDelete',
    method: 'delete',
    url: `/json/doc-type/route/delete/${routeId}`,
  })
}

const docTypeRouteStageCreate = (
  credentials: DocumentTypeRouteStageCredentials
): Promise<DocumentTypeRouteStage> => {
  return sendRequest({
    name: 'docTypeRouteStageCreate',
    method: 'post',
    url: '/json/doc-type/route/stage/create',
    payload: credentials,
  })
}

const docTypeRouteStageUpdate = (
  stageId: number,
  credentials: Omit<DocumentTypeRouteStageCredentials, 'docTypeRouteId'>
): Promise<DocumentTypeRouteStage> => {
  return sendRequest({
    name: 'docTypeRouteStageUpdate',
    method: 'put',
    url: `/json/doc-type/route/stage/update/${stageId}`,
    payload: credentials,
  })
}

const docTypeRouteStageDelete = (stageId: number): Promise<void> => {
  return sendRequest({
    name: 'docTypeRouteStageDelete',
    method: 'delete',
    url: `/json/doc-type/route/stage/delete/${stageId}`,
  })
}

const docTypeRouteStageParticipantCreate = (
  credentials: DocumentTypeRouteStageParticipantCredentials
): Promise<DocumentTypeRouteStageParticipant> => {
  return sendRequest({
    name: 'docTypeRouteStageParticipantCreate',
    method: 'post',
    url: '/json/doc-type/route/stage/participant/create',
    payload: credentials,
  })
}

const docTypeRouteStageParticipantDelete = (
  participantId: number
): Promise<void> => {
  return sendRequest({
    name: 'docTypeRouteStageParticipantDelete',
    method: 'delete',
    url: `/json/doc-type/route/stage/participant/delete/${participantId}`,
  })
}

const docTypeFieldList = (docTypeId: number): Promise<DocumentTypeField[]> => {
  return sendRequest({
    name: 'docTypeFieldList',
    method: 'get',
    url: `/json/doc-type/definition/${docTypeId}/fields`,
  })
}

const docTypeFieldAdd = (
  docTypeId: number,
  credentials: Partial<DocumentTypeFieldCredentials>
): Promise<DocumentTypeField> => {
  return sendRequest({
    name: 'docTypeFieldAdd',
    method: 'post',
    url: `/json/doc-type/definition/${docTypeId}/fields`,
    payload: credentials,
  })
}

const docTypeFieldUpdate = (
  docTypeId: number,
  fieldId: number,
  credentials: Partial<DocumentTypeFieldCredentials>
): Promise<DocumentTypeField> => {
  return sendRequest({
    name: 'docTypeFieldUpdate',
    method: 'put',
    url: `/json/doc-type/definition/${docTypeId}/fields/${fieldId}`,
    payload: credentials,
  })
}

const docTypeFieldReorder = (
  docTypeId: number,
  credentials: Pick<DocumentTypeField, 'id' | 'sortOrder'>
): Promise<DocumentTypeField[]> => {
  return sendRequest({
    name: 'docTypeFieldReorder',
    method: 'post',
    url: `/json/doc-type/definition/${docTypeId}/fields/reorder`,
    payload: credentials,
  })
}

const docTypeFieldDelete = (
  docTypeId: number,
  fieldId: number
): Promise<any> => {
  return sendRequest({
    name: 'docTypeFieldDelete',
    method: 'delete',
    url: `/json/doc-type/definition/${docTypeId}/fields/${fieldId}`,
  })
}

const docTypeDocumentDelete = (documentId: number) => {
  return sendRequest({
    name: 'docTypeDocumentDelete',
    method: 'delete',
    url: `/json/doc-type/document/delete/${documentId}`,
  })
}

export {
  // class
  docTypeClassList,
  docTypeClassActivate,
  docTypeClassDeactivate,
  docTypeClassUpdate,
  docTypeClassCreate,

  // definition
  docTypeDefinitionList,
  docTypeDocumentDelete,
  docTypeDefinitionDelete,
  docTypeDefinitionActivate,
  docTypeDefinitionDeactivate,
  docTypeDefinitionSingle,
  docTypeDefinitionUpdate,
  docTypeDefinitionCreate,
  docTypeAccessesUpdate,
  docTypeAccessCreate,
  docTypeAccessDelete,

  // route
  docTypeRouteList,
  docTypeRouteSingle,
  docTypeRouteCreate,
  docTypeRouteUpdate,
  docTypeRouteActivate,
  docTypeRouteDeactivate,
  docTypeRouteDelete,

  // stage
  docTypeRouteStageCreate,
  docTypeRouteStageUpdate,
  docTypeRouteStageDelete,

  // participant
  docTypeRouteStageParticipantCreate,
  docTypeRouteStageParticipantDelete,

  // field
  docTypeFieldList,
  docTypeFieldAdd,
  docTypeFieldUpdate,
  docTypeFieldReorder,
  docTypeFieldDelete,

  docTypeClassFlowTypes,
}
